import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/provider/SubscriptionProvider";
import {
  buildRenewalShareMessage,
  dismissFinalDay,
  dismissForDay,
  formatFinalDayEnd,
  formatRenewalDate,
  getGymDayKey,
  getRenewalAudience,
  getRenewalTiming,
  isDismissedForDay,
  isFinalDayDismissed,
  isRenewalReminderProvider,
} from "@/lib/renewalReminder";

const TICK_MS = 60 * 1000;

export const useRenewalReminder = () => {
  const { user, activeGym, isAdminView } = useAuth();
  const {
    status,
    provider,
    nextBillingDate,
    isLoading,
    gymName,
    isBillingOwner,
    billedThroughCenterName,
    timezone,
  } = useSubscription();

  const [now, setNow] = useState(() => new Date());
  // Bumped after each dismissal so storage is re-read on the next render.
  const [storageVersion, setStorageVersion] = useState(0);
  const [isModalRequested, setIsModalRequested] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const isEligible = Boolean(
    !isAdminView &&
      !isLoading &&
      status === "active" &&
      isRenewalReminderProvider(provider) &&
      user &&
      activeGym,
  );

  const timing = isEligible ? getRenewalTiming(nextBillingDate, now) : null;

  const keyParts = useMemo(
    () =>
      user && activeGym && nextBillingDate
        ? { userId: user._id, gymId: activeGym._id, nextBillingDate }
        : null,
    [user, activeGym, nextBillingDate],
  );
  const dayKey = getGymDayKey(now, timezone);

  const { isBannerDismissed, isModalDismissed, isFinalDismissed } = useMemo(
    () =>
      keyParts
        ? {
            isBannerDismissed: isDismissedForDay("banner", keyParts, dayKey),
            isModalDismissed: isDismissedForDay("modal", keyParts, dayKey),
            isFinalDismissed: isFinalDayDismissed(keyParts),
          }
        : { isBannerDismissed: true, isModalDismissed: true, isFinalDismissed: true },
    // storageVersion forces a re-read after each dismissal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keyParts, dayKey, storageVersion],
  );

  const audience = getRenewalAudience({ role: user?.role, isBillingOwner });
  const expiryLabel = timing ? formatRenewalDate(timing.expiresAt, timezone) : "";
  const finalDayEndLabel =
    timing?.stage === "final"
      ? formatFinalDayEnd(timing.expiresAt, now, timezone)
      : "";

  const isFinalOpen = timing?.stage === "final" && !isFinalDismissed;
  const isModalOpen =
    Boolean(timing) &&
    !isFinalOpen &&
    (isModalRequested || (timing?.stage === "urgent" && !isModalDismissed));

  const dismissBanner = useCallback(() => {
    if (!keyParts) return;
    dismissForDay("banner", keyParts, dayKey);
    setStorageVersion((version) => version + 1);
  }, [keyParts, dayKey]);

  const closeModal = useCallback(() => {
    if (keyParts) dismissForDay("modal", keyParts, dayKey);
    setIsModalRequested(false);
    setStorageVersion((version) => version + 1);
  }, [keyParts, dayKey]);

  const closeFinalDay = useCallback(() => {
    if (keyParts) dismissFinalDay(keyParts);
    setStorageVersion((version) => version + 1);
  }, [keyParts]);

  const openModal = useCallback(() => setIsModalRequested(true), []);

  return {
    timing,
    audience,
    expiryLabel,
    finalDayEndLabel,
    billedThroughCenterName,
    shareMessage: buildRenewalShareMessage({
      gymName,
      expiryLabel,
      billedThroughCenterName,
      audience,
    }),
    isBannerVisible: Boolean(timing) && !isBannerDismissed,
    isModalOpen,
    isFinalOpen,
    dismissBanner,
    openModal,
    closeModal,
    closeFinalDay,
  };
};

export type RenewalReminderState = ReturnType<typeof useRenewalReminder>;
